import React, { useEffect, useMemo, useState } from 'react';
import {
  DxButton,
  DxCard,
  DxCardCollapsable,
  DxCardContent,
  DxCardHeader,
  DxContainer,
  DxText,
} from '@dvag/design-system-react';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { useAutosave } from '@dvag/dfs-ui-blocks/hooks/useAutosave';
import { UpdateManyResources } from '@dvag/dfs-ui-blocks/hooks/useAutosaveHelper';
import { object, string, array } from 'yup';

import i18next from 'i18next';
import { checkIcon } from 'utils/util';
import { buildPath } from 'service/buildPath';
import { route } from 'config/route';
import { useGetFavoritesWithAction } from 'hooks/useGetFavoritesWithAction';
import { useGetQuickAccessSetting } from 'hooks/useGetQuickAccessSetting';
import { useCreateOrUpdateSettings } from 'hooks/useCreateOrUpdateSettings';
import { SelectedOption, SettingDto } from 'graphql/advisor/generates';
import { useContinuation } from 'utils/useContinuation';
import { UnsavedOrPendingModal } from 'component/UnsavedOrPendingModal';

import { useParams } from 'react-router-dom';
import { useFavoritesUpdatedListener } from './useFavoritesUpdatedListener';

import './style.css';

const MAX_QUICK_ACCESS_SLOTS = 5;

export const QuickAccess = () => {
  const { householdId } = useParams<{ householdId: string }>();
  const { data: favorites } = useGetFavoritesWithAction(householdId);
  const disabledStyle = !householdId ? { opacity: '0.6', pointerEvents: 'none' } : {};

  const onEditClick = () => {
    if (householdId) {
      navigate(`/${appNames.advisor}${buildPath(route.favorites, { householdId })}`);
    }
  };

  const { continuation, getCanContinueHandler, onContinuationRequest } = useContinuation();
  const onCanContinueChange = useMemo(() => getCanContinueHandler(), [getCanContinueHandler]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quickAccessOpenSetting = useGetQuickAccessSetting(true);
  const createOrUpdateUserSettings = useCreateOrUpdateSettings();

  const resourceSchema = useMemo(
    () =>
      object().shape({
        id: string().nullable(),
        selectedOptions: array(),
        code: string(),
      }),
    [],
  );

  const { register, setValue, getValues, flushChanges } = useAutosave<SettingDto>({
    data: quickAccessOpenSetting.data,
    updateManyResources: createOrUpdateUserSettings.mutateAsync as UpdateManyResources<SettingDto>,
    onIsDirtyChange: (isDirtyParam: boolean) => {
      if (!isDirtyParam) {
        getCanContinueHandler();
      }
      onCanContinueChange(!isDirtyParam);
    },
    onIsSubmittingChange: setIsSubmitting,
    resourceSchema,
  });

  const onOpenChange = (value: CustomEvent) => {
    setValue('selectedOptions', value.detail ? [SelectedOption.Off] : [SelectedOption.On]);
  };

  const isQuickAccessOpen = getValues('selectedOptions')?.[0] === SelectedOption.Off;

  useFavoritesUpdatedListener();

  useEffect(() => {
    onContinuationRequest(flushChanges);
  }, [flushChanges, onContinuationRequest]);

  return (
    <>
      <DxCard style={disabledStyle}>
        <DxCardCollapsable
          data-testid="quick-access-card"
          open={isQuickAccessOpen}
          data-open={`${isQuickAccessOpen}`}
          onOpenChange={onOpenChange}
          {...register('selectedOptions')}
        >
          <DxCardHeader
            slot="header"
            label={i18next.t('favorites.quickAccess')}
            data-testid="quick-access-header"
            id="header-quick-access"
          >
            <DxButton
              id="icon-quick-access"
              data-testid="quick-access-icon"
              className="quick-access-icon"
              icon={checkIcon('stift')}
              type="text"
              onClick={onEditClick}
            />
          </DxCardHeader>
          <DxCardContent>
            <DxContainer
              color="transparent"
              className="quick-access-container"
              id="container-quick-access-"
            >
              {favorites.length > 0 ? (
                [...Array(MAX_QUICK_ACCESS_SLOTS).keys()].map((_, index) => (
                  <DxContainer
                    key={_}
                    color="transparent"
                    id="button-quick-access"
                    className={`quick-access-button-container ${
                      favorites[index] && 'quick-access-button-container-border'
                    }`}
                  >
                    {favorites[index] && (
                      <DxContainer
                        id="button-quick-access"
                        color="transparent"
                        className="quick-access-button"
                        onClick={favorites[index]?.onClick}
                      >
                        <DxText
                          type="its"
                          color="gray-83"
                          className="quick-access-button-text"
                          id="text-quick-access-button"
                        >
                          {favorites[index]?.name}
                        </DxText>
                      </DxContainer>
                    )}
                  </DxContainer>
                ))
              ) : (
                <DxText>{i18next.t('favorites.noItems')}</DxText>
              )}
            </DxContainer>
          </DxCardContent>
        </DxCardCollapsable>
      </DxCard>
      <UnsavedOrPendingModal
        showPending={continuation !== undefined && isSubmitting}
        showUnsaved={continuation !== undefined && !isSubmitting}
        onCancel={continuation?.onCancel}
        onConfirm={continuation?.onConfirm}
      />
    </>
  );
};
